/**
 * Phase 3.36 — Öffentliche Werkstatt-Buchungsseite ``/werkstatt/:dealerId/termin``.
 *
 * Kunden wählen einen freien Slot, geben Kontakt + Fahrzeug-Infos an, und
 * buchen den Termin. Bestätigungs-Mail wird an die Werkstatt-Email gesendet
 * (aktuell MOCKED bis Resend-Keys vorliegen).
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar, CalendarPlus, Clock, CheckCircle2, Loader2, Wrench, Sparkles, BellRing, MessageCircle, Mail, Phone, ShieldCheck, Bike, Check } from "lucide-react";
import { toast } from "sonner";
import { downloadICS, googleCalendarUrl, outlookCalendarUrl } from "../lib/calendar";

const SERVICE_TYPES = [
    "Inspektion",
    "Reifenwechsel",
    "Ölwechsel",
    "Bremsen-Service",
    "Hauptuntersuchung",
    "Diagnose / Fehlersuche",
    "Unfall / Diebstahlreparatur",
    "Liegen geblieben / wird eingeschleppt",
    "Sonstiges",
];

// ISO-Kalenderwoche (KW) zu einem Date.
function getISOWeek(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

export default function WorkshopBookingPage() {
    const { dealerId } = useParams();
    const navigate = useNavigate();
    // Jul-2026 (User-Wunsch) — Standort-Wechsel: Kunde kann auf einen anderen
    // Standort der Organisation ausweichen, wenn hier kein Termin frei ist.
    const [sites, setSites] = useState([]);
    useEffect(() => {
        api.get(`/workshop/${dealerId}/sites`).then((r) => setSites(r.data?.sites || [])).catch(() => setSites([]));
    }, [dealerId]);
    const switchSite = (id) => {
        if (id && id !== dealerId) {
            setSelectedDate(""); setSelectedSlot(""); setError(null);
            navigate(`/werkstatt/${id}/termin`);
        }
    };
    const siteSwitcher = sites.length > 1 ? (
        <div className="flex items-center gap-2 flex-wrap" data-testid="booking-site-switcher">
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Standort:</span>
            <select
                value={dealerId}
                onChange={(e) => switchSite(e.target.value)}
                className="h-9 rounded-sm border border-gray-300 bg-white px-2 text-sm"
                data-testid="booking-site-select"
            >
                {sites.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                ))}
            </select>
            <span className="text-[10px] text-gray-400">Kein passender Termin? Einfach Standort wechseln.</span>
        </div>
    ) : null;
    const [slotsData, setSlotsData] = useState(null);
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedSlot, setSelectedSlot] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [confirmed, setConfirmed] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        service_type: SERVICE_TYPES[0],
        customer_name: "",
        customer_email: "",
        customer_phone: "",
        customer_street: "",
        customer_zip: "",
        customer_city: "",
        vehicle_brand: "",
        vehicle_model: "",
        notes: "",
        desired_completion: "",
        reminder_opt_in: true,
        // Jun-2026 (User-Wunsch) — E-Mail immer aktiv, mobil wahlweise WhatsApp ODER SMS.
        reminder_mobile: "whatsapp", // "whatsapp" | "sms" | "none"
        consent_datenschutz: false,
        consent_agb: false,
        // Jun-2026 (User-Wunsch) — Ersatzfahrzeug & Abhol-/Lieferservice
        loaner_requested: false,
        loaner_vehicle_listing_id: "",
        pickup_service_requested: false,
        pickup_partner_id: "",
        pickup_address: "",
    });

    // Extras (Ersatzfahrzeug-Verfügbarkeit + Abhol-Partner) je Wunschtag laden
    const [extras, setExtras] = useState(null);
    useEffect(() => {
        if (!selectedDate) { setExtras(null); return; }
        api.get(`/workshop/${dealerId}/booking-extras?date=${selectedDate}`)
            .then((r) => setExtras(r.data))
            .catch(() => setExtras(null));
    }, [dealerId, selectedDate]);

    useEffect(() => {
        setLoading(true);
        const q = form.service_type ? `?service_type=${encodeURIComponent(form.service_type)}` : "";
        api.get(`/workshop/${dealerId}/slots${q}`)
            .then((r) => {
                setSlotsData(r.data);
                if (r.data.days?.length) {
                    setSelectedDate((prev) => (r.data.days.find((d) => d.date === prev) ? prev : r.data.days[0].date));
                } else {
                    setSelectedDate("");
                }
                setSelectedSlot("");
            })
            .catch((e) => setError(e.response?.data?.detail || "Fehler beim Laden"))
            .finally(() => setLoading(false));
    }, [dealerId, form.service_type]);

    // Jul-2026 (User-Wunsch) — Kunden-Abgleich (synchron zur Mitarbeiter-Annahme):
    // bekannter Kunde → Daten werden übernommen · unbekannt → Anlage-Felder Pflicht.
    const [knownCustomer, setKnownCustomer] = useState(null);
    useEffect(() => {
        const email = form.customer_email.trim();
        const phone = form.customer_phone.replace(/\D/g, "");
        if (!email.includes("@") && phone.length < 7) { setKnownCustomer(null); return undefined; }
        const t = setTimeout(async () => {
            try {
                const { data } = await api.post(`/workshop/${dealerId}/customer-check`,
                    { email, phone: form.customer_phone });
                setKnownCustomer(data.checked ? !!data.known : null);
                // Aug-2026 (User-Wunsch): bekannter Kunde mit hinterlegtem Fahrzeug →
                // Marke/Modell automatisch übernehmen (nur wenn noch leer).
                const v = (data.vehicles || [])[0];
                if (data.known && v) {
                    setForm((f) => (f.vehicle_brand || f.vehicle_model ? f
                        : { ...f, vehicle_brand: v.brand || "", vehicle_model: v.model || "" }));
                }
            } catch { setKnownCustomer(null); }
        }, 600);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.customer_email, form.customer_phone, dealerId]);

    const submit = async () => {
        if (!selectedDate || !selectedSlot) { toast.error("Bitte Datum und Uhrzeit wählen"); return; }
        if (!form.customer_name || !form.customer_email) { toast.error("Name und E-Mail sind Pflicht"); return; }
        if (!form.customer_phone || form.customer_phone.trim().length < 6) { toast.error("Bitte eine gültige Handynummer angeben"); return; }
        // Jul-2026 (User-Wunsch) — neuer Kunde: Anlage-Felder sind Pflicht.
        if (knownCustomer === false && (!form.customer_street.trim() || !form.customer_zip.trim() || !form.customer_city.trim())) {
            toast.error("Bitte Straße, PLZ und Ort angeben — Sie sind noch nicht bei uns hinterlegt");
            return;
        }
        if (!form.consent_datenschutz || !form.consent_agb) { toast.error("Bitte stimmen Sie Datenschutz und AGB zu"); return; }
        setSubmitting(true);
        const channels = form.reminder_opt_in
            ? ["email", form.reminder_mobile !== "none" ? form.reminder_mobile : null].filter(Boolean)
            : [];
        try {
            const { data } = await api.post(`/workshop/${dealerId}/book`, {
                service_type: form.service_type,
                customer_name: form.customer_name,
                customer_email: form.customer_email,
                customer_phone: form.customer_phone,
                customer_street: form.customer_street || null,
                customer_zip: form.customer_zip || null,
                customer_city: form.customer_city || null,
                vehicle_brand: form.vehicle_brand,
                vehicle_model: form.vehicle_model,
                notes: form.notes,
                desired_completion: form.desired_completion || null,
                reminder_opt_in: form.reminder_opt_in,
                reminder_channels: channels,
                consent_datenschutz: form.consent_datenschutz,
                consent_agb: form.consent_agb,
                date: selectedDate,
                time_slot: selectedSlot,
                loaner_requested: !!(form.loaner_requested && extras?.loaner_available),
                loaner_vehicle_listing_id: (form.loaner_requested && form.loaner_vehicle_listing_id) || null,
                loaner_vehicle_label: (extras?.loaner_vehicles || []).find((v) => v.id === form.loaner_vehicle_listing_id)?.label || null,
                pickup_service_requested: form.pickup_service_requested,
                pickup_partner_id: form.pickup_partner_id || null,
                pickup_partner_name: (extras?.partners || []).find((p) => p.id === form.pickup_partner_id)?.name || null,
                pickup_address: form.pickup_service_requested ? (form.pickup_address || null) : null,
            });
            setConfirmed(data);
            toast.success("Termin gebucht — wir senden eine Bestätigung an Ihre E-Mail.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Buchung fehlgeschlagen");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12 flex items-center gap-3 text-gray-600">
                <Loader2 className="w-5 h-5 animate-spin" /> Verfügbare Termine werden geladen…
            </div>
        );
    }
    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-4 py-12">
                <h1 className="font-display font-bold text-2xl mb-2">Buchung nicht verfügbar</h1>
                <p className="text-gray-600">{error}</p>
                {/* Jul-2026 (User-Wunsch) — auch im Fehlerfall anderen Standort anbieten. */}
                {siteSwitcher && <div className="mt-4">{siteSwitcher}</div>}
                <Link to="/" className="inline-block mt-4 text-[#111827] underline">Zurück zur Startseite</Link>
            </div>
        );
    }
    if (confirmed) {
        // Kalender-Event aus den bestätigten Termindaten ableiten (lokale Zeit).
        const [ch, cm] = String(confirmed.time_slot).split(":").map(Number);
        const [cy, cmo, cd] = String(confirmed.date).split("-").map(Number);
        const calStart = new Date(cy, (cmo || 1) - 1, cd, ch || 0, cm || 0, 0, 0);
        const calEnd = new Date(calStart.getTime() + (confirmed.duration_min || 60) * 60000);
        const dealerName = slotsData?.dealer_name || "Werkstatt";
        const vehicle = [confirmed.vehicle_brand, confirmed.vehicle_model].filter(Boolean).join(" ");
        const calEvent = {
            title: `Werkstatt-Termin: ${confirmed.service_type} (${dealerName})`,
            description: `Service: ${confirmed.service_type}${vehicle ? ` · Fahrzeug: ${vehicle}` : ""}\nWerkstatt: ${dealerName}${confirmed.notes ? `\nNotiz: ${confirmed.notes}` : ""}`,
            location: dealerName,
            start: calStart,
            end: calEnd,
        };
        return (
            <div className="max-w-3xl mx-auto px-4 py-16 text-center" data-testid="booking-confirmed">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="font-display font-black text-3xl mb-2">Termin bestätigt</h1>
                <p className="text-gray-700 mb-6">
                    {confirmed.service_type} am <strong>{new Date(confirmed.date).toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</strong> um <strong>{confirmed.time_slot}</strong>
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3 mb-6" data-testid="booking-calendar-actions">
                    <Button
                        type="button"
                        onClick={() => downloadICS(`werkstatt-termin-${confirmed.date}.ics`, calEvent)}
                        className="bg-[#111827] text-white hover:bg-black rounded-sm"
                        data-testid="booking-add-ics"
                    >
                        <CalendarPlus className="w-4 h-4 mr-2" /> Zum Kalender hinzufügen (.ics)
                    </Button>
                    <a
                        href={googleCalendarUrl(calEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-sm border border-gray-300 bg-white text-sm font-bold text-gray-700 hover:border-[#111827] transition-colors"
                        data-testid="booking-add-google"
                    >
                        <Calendar className="w-4 h-4 mr-2" /> Google Kalender
                    </a>
                    <a
                        href={outlookCalendarUrl(calEvent)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 rounded-sm border border-gray-300 bg-white text-sm font-bold text-gray-700 hover:border-[#111827] transition-colors"
                        data-testid="booking-add-outlook"
                    >
                        <Calendar className="w-4 h-4 mr-2" /> Outlook / Office 365
                    </a>
                </div>
                <p className="text-sm text-gray-500">Wir senden eine Bestätigung an {confirmed.customer_email}. Die Werkstatt meldet sich bei Rückfragen.</p>
                <Link to="/" className="inline-block mt-8 text-[#111827] font-bold underline">Zurück zur Startseite</Link>
            </div>
        );
    }

    const daysAvailable = slotsData?.days || [];
    const dayObj = daysAvailable.find((d) => d.date === selectedDate);
    // Jun-2026 (User-Wunsch) — Tage nach Kalenderwoche gruppieren (deutsche Wochentage).
    const weeksOrder = [];
    const daysByWeek = {};
    for (const d of daysAvailable) {
        const dt = new Date(d.date + "T00:00:00");
        const kw = getISOWeek(dt);
        if (!daysByWeek[kw]) { daysByWeek[kw] = []; weeksOrder.push(kw); }
        daysByWeek[kw].push({ ...d, dt });
    }

    return (
        <div className="max-w-3xl mx-auto px-4 py-8" data-testid="workshop-booking-page">
            <div className="bg-[#FFCC00] text-[#111827] px-4 py-3 mb-6 rounded-sm flex items-center gap-3">
                {slotsData.dealer_logo ? (
                    <img
                        src={slotsData.dealer_logo}
                        alt={slotsData.dealer_name}
                        className="h-12 w-auto max-w-[120px] object-contain bg-white rounded-sm p-1 shrink-0"
                        data-testid="dealer-logo"
                    />
                ) : null}
                <div className="min-w-0">
                    <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Werkstatt-Terminbuchung</div>
                    <h1 className="font-display font-black text-2xl leading-tight truncate" data-testid="dealer-name">{slotsData.dealer_name}</h1>
                </div>
            </div>

            {siteSwitcher && <div className="mb-6 -mt-2">{siteSwitcher}</div>}

            <div className="space-y-6">
                {/* Step 1 — Service-Typ */}
                <div>
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700">1. Service-Typ</Label>
                    <select
                        value={form.service_type}
                        onChange={(e) => setForm({ ...form, service_type: e.target.value })}
                        className="w-full mt-2 rounded-sm border border-gray-300 px-3 py-2 bg-white text-sm"
                        data-testid="booking-service-type"
                    >
                        {SERVICE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>

                {/* Phase 4.14 (M7) — KI-Smart-Slot-Vorschläge */}
                <SmartSlotSuggestions
                    dealerId={dealerId}
                    serviceType={form.service_type}
                    onPick={(s) => {
                        if (s.date) setSelectedDate(s.date);
                        if (s.start_time) setSelectedSlot(s.start_time);
                    }}
                />

                {/* Step 2 — Datum */}
                <div>
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700 flex items-center gap-2">
                        <Calendar className="w-4 h-4" /> 2. Datum
                    </Label>
                    {daysAvailable.length === 0 ? (
                        <div className="text-sm text-gray-500 mt-2">Aktuell keine Termine verfügbar.</div>
                    ) : (
                        <div className="space-y-3 mt-2">
                            {weeksOrder.map((kw) => {
                                const wdays = daysByWeek[kw];
                                const first = wdays[0].dt;
                                const last = wdays[wdays.length - 1].dt;
                                return (
                                    <div key={kw} data-testid={`booking-week-${kw}`}>
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className="text-[10px] font-black uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-1.5 py-0.5 rounded-sm font-mono-num">
                                                KW {kw}
                                            </span>
                                            <span className="text-[11px] text-gray-500 font-mono-num">
                                                {first.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })} – {last.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}
                                            </span>
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {wdays.map((d) => (
                                                <button
                                                    key={d.date}
                                                    onClick={() => { setSelectedDate(d.date); setSelectedSlot(""); }}
                                                    className={`flex flex-col items-center justify-center px-3 py-2 rounded-sm border min-w-[72px] transition-colors ${selectedDate === d.date ? "bg-[#F97316] text-white border-[#F97316] ring-2 ring-[#F97316]/40 font-bold" : "bg-white text-gray-800 border-gray-300 hover:border-[#F97316]"}`}
                                                    data-testid={`booking-day-${d.date}`}
                                                >
                                                    <span className="text-[11px] font-bold uppercase tracking-wider leading-none mb-1 opacity-90" data-testid={`booking-day-weekday-${d.date}`}>
                                                        {d.dt.toLocaleDateString("de-DE", { weekday: "short" }).replace(".", "")}
                                                    </span>
                                                    <span className="font-mono-num text-base font-black leading-none">
                                                        {d.dt.getDate().toString().padStart(2, "0")}.{(d.dt.getMonth() + 1).toString().padStart(2, "0")}
                                                    </span>
                                                    {d.ampel === "gelb" ? (
                                                        <span
                                                            className={`mt-1.5 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider leading-none ${selectedDate === d.date ? "text-white/90" : "text-amber-700"}`}
                                                            title={`Gut gebucht — Werkstatt-Auslastung ${Math.round((d.workshop_load_pct || 0) * 100)} %`}
                                                            data-testid={`booking-day-busy-${d.date}`}
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> fast voll
                                                        </span>
                                                    ) : d.low_load && (
                                                        <span
                                                            className={`mt-1.5 inline-flex items-center gap-1 text-[8px] font-bold uppercase tracking-wider leading-none ${selectedDate === d.date ? "text-white/90" : "text-green-700"}`}
                                                            title="Wenig gebucht — gute Termin-Verfügbarkeit"
                                                            data-testid={`booking-day-low-load-${d.date}`}
                                                        >
                                                            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" /> frei
                                                        </span>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Step 3 — Uhrzeit */}
                {dayObj && (
                    <div>
                        <Label className="font-bold text-sm uppercase tracking-wider text-gray-700 flex items-center gap-2">
                            <Clock className="w-4 h-4" /> 3. Uhrzeit
                            {slotsData?.duration_min && slotsData.duration_min !== 60 && (
                                <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-white px-2 py-0.5 rounded-sm" data-testid="booking-duration-badge">
                                    Dauer ca. {slotsData.duration_min} min
                                </span>
                            )}
                        </Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                            {dayObj.slots.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSelectedSlot(s)}
                                    className={`px-4 py-2 rounded-sm border text-sm font-mono-num font-bold ${selectedSlot === s ? "bg-[#F97316] text-white border-[#F97316] ring-2 ring-[#F97316]/40" : "bg-white text-gray-700 border-gray-300 hover:border-[#F97316]"}`}
                                    data-testid={`booking-slot-${s.replace(":", "")}`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Step 4 — Kontakt */}
                <div className="border-t border-gray-200 pt-6">
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700">4. Ihre Daten</Label>
                    <div className="grid sm:grid-cols-2 gap-3 mt-3">
                        <div>
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Name *</Label>
                            <Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} className="rounded-sm mt-1" data-testid="booking-name" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">E-Mail *</Label>
                            <Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="rounded-sm mt-1" data-testid="booking-email" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Handynummer *</Label>
                            <Input type="tel" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} placeholder="z. B. 0151 23456789" className="rounded-sm mt-1" data-testid="booking-phone" />
                        </div>
                        {/* Jul-2026 (User-Wunsch) — Kunden-Abgleich mit dem System */}
                        {knownCustomer === true && (
                            <div className="sm:col-span-2 bg-green-50 border border-green-200 rounded-sm px-3 py-2 text-xs text-green-800 flex items-center gap-2" data-testid="booking-customer-known">
                                <Check className="w-4 h-4 shrink-0" />
                                Sie sind bereits Kunde bei uns — Ihre hinterlegten Daten (Adresse, Fahrzeug) werden bei der Annahme automatisch übernommen.
                            </div>
                        )}
                        {knownCustomer === false && (
                            <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3" data-testid="booking-customer-new-fields">
                                <div className="sm:col-span-3 text-[11px] text-gray-500 -mb-1">
                                    Sie sind noch nicht bei uns hinterlegt — für die Auftragsanlage benötigen wir Ihre Anschrift:
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Straße &amp; Hausnr. *</Label>
                                    <Input value={form.customer_street} onChange={(e) => setForm({ ...form, customer_street: e.target.value })} className="rounded-sm mt-1" data-testid="booking-street" />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">PLZ *</Label>
                                    <Input value={form.customer_zip} onChange={(e) => setForm({ ...form, customer_zip: e.target.value })} className="rounded-sm mt-1" data-testid="booking-zip" />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Ort *</Label>
                                    <Input value={form.customer_city} onChange={(e) => setForm({ ...form, customer_city: e.target.value })} className="rounded-sm mt-1" data-testid="booking-city" />
                                </div>
                            </div>
                        )}
                        <div>
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Fahrzeug-Marke</Label>
                            <Input value={form.vehicle_brand} onChange={(e) => setForm({ ...form, vehicle_brand: e.target.value })} className="rounded-sm mt-1" data-testid="booking-brand" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Modell</Label>
                            <Input value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} className="rounded-sm mt-1" data-testid="booking-model" />
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500 flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5" /> Wunschtermin Fertigstellung / Abholung
                            </Label>
                            <Input
                                type="date"
                                value={form.desired_completion}
                                min={selectedDate || undefined}
                                onChange={(e) => setForm({ ...form, desired_completion: e.target.value })}
                                className="rounded-sm mt-1"
                                data-testid="booking-desired-completion"
                            />
                            <p className="text-[11px] text-gray-500 mt-1">Optional — wann möchten Sie Ihr Fahrzeug spätestens zurück? Wir versuchen, dies zu berücksichtigen.</p>
                        </div>
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] uppercase font-bold tracking-wider text-gray-500">Ihr Anliegen / Auftrag (im Wortlaut)</Label>
                            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1" rows={3} placeholder="Beschreiben Sie kurz, was gemacht werden soll — dieser Text erscheint später im Auftrag als „Kundenauftrag (im Wortlaut)“." data-testid="booking-notes" />
                        </div>
                    </div>
                </div>

                {/* Step 5 — Ersatzfahrzeug & Abhol-/Lieferservice (Jun-2026, User-Wunsch) */}
                <div className="border-t border-gray-200 pt-6" data-testid="booking-extras-section">
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700 flex items-center gap-2">
                        <Bike className="w-4 h-4" /> 5. Ersatzfahrzeug &amp; Abhol-/Lieferservice
                    </Label>
                    <div className="mt-3 space-y-3">
                        {extras?.loaner_available ? (
                            <div>
                                <label className="flex items-start gap-2.5 cursor-pointer" data-testid="booking-loaner-label">
                                    <input type="checkbox" checked={form.loaner_requested}
                                        onChange={(e) => setForm({ ...form, loaner_requested: e.target.checked, loaner_vehicle_listing_id: e.target.checked ? ((extras?.loaner_vehicles || [])[0]?.id || "") : "" })}
                                        className="mt-0.5 w-4 h-4 accent-[#F97316]" data-testid="booking-loaner-cb" />
                                    <span className="text-sm text-gray-700">
                                        <strong>Ersatzfahrzeug reservieren</strong> — für Ihren Wunschtag {extras.loaner_free > 1 ? `sind ${extras.loaner_free} Ersatzfahrzeuge` : "ist 1 Ersatzfahrzeug"} verfügbar (Reservierung nach Bestätigung durch die Werkstatt).
                                    </span>
                                </label>
                                {/* Jul-2026 (User-Wunsch) — verfügbare Fahrzeuge konkret anbieten */}
                                {form.loaner_requested && (extras?.loaner_vehicles || []).length > 0 && (
                                    <div className="pl-7 mt-2 space-y-1.5" data-testid="booking-loaner-vehicles">
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Verfügbare Ersatzfahrzeuge — Ihre Wahl:</div>
                                        {extras.loaner_vehicles.map((v) => (
                                            <label key={v.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`booking-loaner-vehicle-${v.id}`}>
                                                <input type="radio" name="loaner-vehicle" checked={form.loaner_vehicle_listing_id === v.id}
                                                    onChange={() => setForm({ ...form, loaner_vehicle_listing_id: v.id })}
                                                    className="w-4 h-4 accent-[#F97316]" />
                                                {v.label}
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="booking-loaner-unavailable">
                                <Bike className="w-3.5 h-3.5" /> {selectedDate ? "Für Ihren Wunschtag ist leider kein Ersatzfahrzeug verfügbar." : "Bitte zuerst ein Datum wählen — dann prüfen wir die Ersatzfahrzeug-Verfügbarkeit."}
                            </p>
                        )}
                        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="booking-pickup-label">
                            <input type="checkbox" checked={form.pickup_service_requested}
                                onChange={(e) => setForm({ ...form, pickup_service_requested: e.target.checked })}
                                className="mt-0.5 w-4 h-4 accent-[#F97316]" data-testid="booking-pickup-cb" />
                            <span className="text-sm text-gray-700">
                                <strong>Abhol-/Lieferservice gewünscht</strong> — Ihr Fahrzeug wird abgeholt und nach der Reparatur zurückgebracht.
                            </span>
                        </label>
                        {form.pickup_service_requested && (
                            <div className="pl-7 space-y-2.5" data-testid="booking-pickup-options">
                                <Input value={form.pickup_address}
                                    onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                                    placeholder="Abholadresse (Straße, PLZ Ort)"
                                    className="rounded-sm" data-testid="booking-pickup-address" />
                                {(extras?.partners || []).length > 0 ? (
                                    <div>
                                        <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                                            {extras.partners.some((p) => p.favorite) ? "Unsere Transport-Partner:" : "Transport-Partner aus dem Netzwerk:"}
                                        </div>
                                        <div className="space-y-1.5">
                                            {/* Jul-2026 (User-Wunsch): Abholung durch den Händler ist ausgeschlossen —
                                                bleibt sichtbar, ist aber NICHT auswählbar. */}
                                            <label className="flex items-center gap-2 text-sm text-gray-400 cursor-not-allowed select-none" data-testid="booking-pickup-partner-none">
                                                <input type="radio" name="pickup-partner" checked={false} disabled
                                                    className="w-4 h-4 accent-gray-300 cursor-not-allowed" />
                                                <span>Werkstatt soll die Abholung organisieren <strong className="text-red-500 uppercase text-[11px] tracking-wider">— zur Zeit nicht möglich</strong></span>
                                            </label>
                                            {extras.partners.map((p) => {
                                                // Aug-2026 (User-Wunsch): Partner-Homepage als Link;
                                                // bei Auswahl automatisch in neuem Tab öffnen.
                                                const site = (p.website || "").trim().replace(/,/g, ".");
                                                const url = site ? (site.startsWith("http") ? site : `https://${site}`) : null;
                                                return (
                                                    <label key={p.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer" data-testid={`booking-pickup-partner-${p.id}`}>
                                                        <input type="radio" name="pickup-partner" checked={form.pickup_partner_id === p.id}
                                                            onChange={() => {
                                                                setForm({ ...form, pickup_partner_id: p.id });
                                                                if (url) window.open(url, "_blank", "noopener");
                                                            }}
                                                            className="w-4 h-4 accent-[#F97316]"
                                                            data-testid={`booking-pickup-partner-radio-${p.id}`} />
                                                        <span>
                                                            {p.favorite && <span className="text-[#F97316] font-bold" title="Bevorzugter Partner">★ </span>}
                                                            {url ? (
                                                                <a href={url} target="_blank" rel="noopener noreferrer"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                    className="underline decoration-dotted underline-offset-2 hover:text-[#F97316]"
                                                                    title={`Homepage von ${p.name} öffnen`}
                                                                    data-testid={`booking-pickup-partner-link-${p.id}`}>
                                                                    {p.name}
                                                                </a>
                                                            ) : p.name}
                                                            {(p.regions || []).length ? ` (${p.regions.slice(0, 3).join(", ")})` : ""}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-xs text-red-500 font-bold uppercase tracking-wider" data-testid="booking-pickup-not-possible">Abhol-Service zur Zeit nicht möglich</p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Step 6 — Termin-Erinnerung */}
                <div className="border-t border-gray-200 pt-6">
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700 flex items-center gap-2">
                        <BellRing className="w-4 h-4" /> 6. Termin-Erinnerung
                    </Label>
                    <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="booking-reminder-optin-label">
                        <input
                            type="checkbox"
                            checked={form.reminder_opt_in}
                            onChange={(e) => setForm({ ...form, reminder_opt_in: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="booking-reminder-optin"
                        />
                        <span className="text-sm text-gray-700">
                            Ja, bitte erinnert mich an meinen Termin per <strong>E-Mail</strong> und wahlweise <strong>WhatsApp oder SMS</strong>.
                        </span>
                    </label>
                    {form.reminder_opt_in && (
                        <div className="mt-3 pl-7 space-y-3" data-testid="booking-reminder-channels">
                            <div className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                                <input type="checkbox" checked disabled className="w-4 h-4 accent-[#111827]" data-testid="booking-reminder-email" />
                                <Mail className="w-3.5 h-3.5" /> E-Mail
                                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 ml-1">(immer)</span>
                            </div>
                            <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Zusätzlich mobil — bitte eines wählen:</div>
                                <div className="flex flex-wrap gap-4">
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="reminder-mobile" checked={form.reminder_mobile === "whatsapp"} onChange={() => setForm({ ...form, reminder_mobile: "whatsapp" })} className="w-4 h-4 accent-[#25D366]" data-testid="booking-reminder-whatsapp" />
                                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                    </label>
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="reminder-mobile" checked={form.reminder_mobile === "sms"} onChange={() => setForm({ ...form, reminder_mobile: "sms" })} className="w-4 h-4 accent-[#111827]" data-testid="booking-reminder-sms" />
                                        <Phone className="w-3.5 h-3.5" /> SMS
                                    </label>
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-500 cursor-pointer">
                                        <input type="radio" name="reminder-mobile" checked={form.reminder_mobile === "none"} onChange={() => setForm({ ...form, reminder_mobile: "none" })} className="w-4 h-4 accent-[#111827]" data-testid="booking-reminder-none" />
                                        Keine mobile Erinnerung
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}
                    <p className="text-[11px] text-gray-500 mt-2 pl-7">Für WhatsApp- bzw. SMS-Erinnerungen verwenden wir Ihre Handynummer.</p>
                </div>

                {/* Step 7 — Einwilligungen */}
                <div className="border-t border-gray-200 pt-6">
                    <Label className="font-bold text-sm uppercase tracking-wider text-gray-700 flex items-center gap-2">
                        <ShieldCheck className="w-4 h-4" /> 7. Einwilligungen *
                    </Label>
                    <label className="flex items-start gap-2.5 mt-3 cursor-pointer" data-testid="booking-consent-datenschutz-label">
                        <input
                            type="checkbox"
                            checked={form.consent_datenschutz}
                            onChange={(e) => setForm({ ...form, consent_datenschutz: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="booking-consent-datenschutz"
                        />
                        <span className="text-sm text-gray-700">
                            Ich habe die <Link to="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline font-bold text-[#111827]">Datenschutzerklärung</Link> gelesen und stimme der Verarbeitung meiner Daten zu. *
                        </span>
                    </label>
                    <label className="flex items-start gap-2.5 mt-2.5 cursor-pointer" data-testid="booking-consent-agb-label">
                        <input
                            type="checkbox"
                            checked={form.consent_agb}
                            onChange={(e) => setForm({ ...form, consent_agb: e.target.checked })}
                            className="mt-0.5 w-4 h-4 accent-[#111827]"
                            data-testid="booking-consent-agb"
                        />
                        <span className="text-sm text-gray-700">
                            Ich akzeptiere die <Link to="/agb" target="_blank" rel="noopener noreferrer" className="underline font-bold text-[#111827]">AGB</Link>. *
                        </span>
                    </label>
                </div>

                <Button
                    onClick={submit}
                    disabled={submitting || !selectedDate || !selectedSlot || !form.consent_datenschutz || !form.consent_agb}
                    className="w-full bg-[#111827] hover:bg-black text-white font-bold rounded-sm uppercase tracking-wider"
                    data-testid="booking-submit"
                >
                    {submitting ? "Wird gebucht…" : <><Wrench className="w-4 h-4 mr-2" /> Termin verbindlich buchen</>}
                </Button>
            </div>
        </div>
    );
}


/**
 * Phase 4.14 (M7) — KI-Smart-Slot-Vorschläge.
 * Holt Top-3 Slot-Empfehlungen vom Backend basierend auf Service-Typ +
 * Werkstatt-Auslastung. Klick → Datum + Uhrzeit übernehmen.
 */
function SmartSlotSuggestions({ dealerId, serviceType, onPick }) {
    const [busy, setBusy] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => { setLoaded(false); setSuggestions([]); }, [serviceType]);

    const run = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/workshop/${dealerId}/suggest-slots`, { service_type: serviceType });
            setSuggestions(data.suggestions || []);
            setLoaded(true);
        } catch {
            toast.error("KI-Vorschläge fehlgeschlagen");
        } finally { setBusy(false); }
    };

    if (!serviceType) return null;
    return (
        <div className="bg-violet-50 border-2 border-violet-200 rounded-sm p-4" data-testid="smart-slot-section">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                <div className="flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5 text-violet-700" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-violet-900">
                        KI-Vorschläge — beste Termine für &bdquo;{serviceType}&ldquo;
                    </span>
                </div>
                <button
                    type="button"
                    onClick={run}
                    disabled={busy}
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-violet-700 hover:bg-violet-800 text-white disabled:bg-gray-100 disabled:text-gray-400"
                    data-testid="smart-slot-load"
                >
                    {busy ? <><Loader2 className="w-3 h-3 animate-spin" /> Berechne…</> : (loaded ? "Neu vorschlagen" : "Top-3 vorschlagen")}
                </button>
            </div>
            {loaded && suggestions.length === 0 && (
                <div className="text-[12px] text-gray-600 italic">Keine besonders passenden Slots gefunden.</div>
            )}
            {suggestions.length > 0 && (
                <div className="space-y-2" data-testid="smart-slot-list">
                    {suggestions.map((s, i) => {
                        const dt = s.date ? new Date(s.date) : null;
                        return (
                            <button
                                key={s.slot_id || i}
                                type="button"
                                onClick={() => onPick(s)}
                                className="w-full text-left bg-white border border-violet-200 hover:border-violet-500 rounded-sm p-3 flex items-center justify-between gap-2 transition-colors"
                                data-testid={`smart-slot-pick-${i}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-[10px] font-bold uppercase tracking-wider bg-violet-100 text-violet-800 px-2 py-0.5 rounded-sm">
                                            Score {s.score}
                                        </span>
                                        <span className="font-bold text-sm">
                                            {dt ? dt.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "long" }) : s.date}
                                            {s.start_time && ` · ${s.start_time}`}
                                        </span>
                                    </div>
                                    {s.reasons?.length > 0 && (
                                        <div className="text-[11px] text-gray-600 mt-0.5">{s.reasons.join(" · ")}</div>
                                    )}
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-wider text-violet-700">Übernehmen →</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
